import type { Pillar } from '@/types/pillars';

import { useCompanyPreferences } from '@/api/performance-company-preferences';
import { useGetPillars } from '@/api/pillars';
import { PillarStatus } from '@/constants/PillarStatus';

/**
 * `single`: one pillar at most. `multiple`: any number. `removeOnly`: the company allows one pillar
 * per objective, but the edited objective already carries several -- they can be unchecked, no
 * other offered.
 */
export type PillarSelectionMode = 'single' | 'multiple' | 'removeOnly';

/**
 * What the objective form can offer as pillars, once both the list and the company's settings are
 * known. `isPillarRequired`: the company refuses a published objective aligned with no pillar.
 */
export type PillarChoice =
    | { state: 'pending' }
    | { state: 'failed' }
    | { state: 'ready'; mode: PillarSelectionMode; pillars: Pillar[]; isPillarRequired: boolean };

/** Whether the form offers "No pillar" beside the pillars: only where the company allows it. */
export const offersNoPillarChoice = (pillarChoice: PillarChoice): boolean =>
    pillarChoice.state === 'ready' &&
    pillarChoice.mode === 'single' &&
    pillarChoice.pillars.length > 0 &&
    !pillarChoice.isPillarRequired;

/**
 * Whether publishing waits for an explicit pillar choice: wherever "No pillar" is offered beside the
 * pillars, and while it is not yet known whether it will be.
 */
export const requiresPillarChoice = (pillarChoice: PillarChoice): boolean =>
    pillarChoice.state === 'pending' || offersNoPillarChoice(pillarChoice);

/** Whether publishing needs at least one pillar, as the company requires. */
export const requiresPillar = (pillarChoice: PillarChoice): boolean =>
    pillarChoice.state === 'ready' && pillarChoice.isPillarRequired;

/**
 * The pillars the objective form offers, and whether one or several can be chosen.
 *
 * A new objective is offered the active pillars. An edited one also keeps the pillars it already
 * carries, whatever their status, so a deactivated pillar can still be seen and unchecked. The
 * company decides whether an objective may carry several pillars; an objective that already
 * carries several while the company allows only one is shown its own pillars alone, so they can be
 * removed but no other added.
 */
export const usePillarChoice = (carriedPillarIds: readonly string[] = []): PillarChoice => {
    const { data: pillars, isError: hasPillarsError } = useGetPillars();
    const { data: preferences, isError: hasPreferencesError } = useCompanyPreferences();

    if (hasPillarsError || hasPreferencesError) {
        return { state: 'failed' };
    }

    if (!pillars || !preferences) {
        return { state: 'pending' };
    }

    const isCarried = (pillar: Pillar) => carriedPillarIds.includes(pillar.id);
    const isPillarRequired = !preferences.allowObjectiveWithoutPillar;

    if (!preferences.allowMultiplePillarsPerObjective && carriedPillarIds.length > 1) {
        return {
            state: 'ready',
            mode: 'removeOnly',
            pillars: pillars.filter(isCarried),
            isPillarRequired,
        };
    }

    return {
        state: 'ready',
        mode: preferences.allowMultiplePillarsPerObjective ? 'multiple' : 'single',
        pillars: pillars.filter(
            (pillar) => pillar.status === PillarStatus.ACTIVE || isCarried(pillar),
        ),
        isPillarRequired,
    };
};
